import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { focusWithoutScrolling } from 'utils/focus-without-scroll';
import { aboveTooltip } from 'styles/z-indexs';

type UseSearchbarExpandParams = {
  expand: () => void;
  updateValue: (value: string) => void;
  searchbarRef: React.RefObject<HTMLDivElement>;
  searchInputRef: React.RefObject<HTMLInputElement>;
  pseudoInputRef: React.RefObject<HTMLButtonElement>;
  onPseudoInputEvent: () => void;
};

const focusSearchInput = (
  searchbarRef: React.RefObject<HTMLDivElement>,
  searchInputRef: React.RefObject<HTMLInputElement>,
  pseudoInputRef: React.RefObject<HTMLButtonElement>,
) => {
  const searchbar = searchbarRef.current;
  const pseudoInput = pseudoInputRef.current;

  if (searchbar && pseudoInput) {
    // Свернутый инпут в строке поиска скрыт за пределами экрана (слева: -9999 пикселей).
    // Safari и iOS отказываются фокусировать элементы, находящиеся за пределами экрана, поэтому мы перемещаем
    // свернутый инпут в позицию псевдоинпута синхронно с жестом пользователя.
    const rect = pseudoInput.getBoundingClientRect();
    searchbar.style.position = 'fixed';
    searchbar.style.zIndex = `${aboveTooltip + 1}`;
    searchbar.style.top = `${rect.top}px`;
    searchbar.style.left = `${rect.left}px`;
    searchbar.style.width = `${rect.width}px`;
  }

  if (searchInputRef.current) {
    focusWithoutScrolling(searchInputRef.current);
  }
};

export const useSearchbarExpand = ({
  expand,
  updateValue,
  searchbarRef,
  searchInputRef,
  pseudoInputRef,
  onPseudoInputEvent,
}: UseSearchbarExpandParams) => {
  const expandAndFocus = useCallback(() => {
    flushSync(() => expand());
    focusSearchInput(searchbarRef, searchInputRef, pseudoInputRef);
  }, [expand, searchbarRef, searchInputRef, pseudoInputRef]);

  const onPseudoInputClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      expandAndFocus();
      onPseudoInputEvent();
    },
    [expandAndFocus, onPseudoInputEvent],
  );

  const onPseudoInputClearClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      updateValue('');
      expandAndFocus();
    },
    [expandAndFocus, updateValue],
  );

  return { onPseudoInputClick, onPseudoInputClearClick };
};
